// =============================================================================
// policies.ts — Contrato Zod compartilhado de ciência de política interna
// (F51-S17, docs/planejamento-isolamento-de-inbox-e-privacidade-interna.md
// §10/§11).
//
// Contexto: o Banco do Povo grava a comunicação corporativa (linhas
// corporativas do plano empresarial da operadora, 1 canal meta_whatsapp + 6
// canais waha) e precisa de PROVA, dentro do sistema, de que cada funcionário
// com acesso à caixa de WhatsApp foi avisado disso. Este contrato cobre:
//   1. O TEXTO da política, versionado como DADO (nunca constante de código)
//      — `policy_documents`. Trocar o texto gera versão nova.
//   2. O ACEITE do funcionário a uma versão específica — `policy_acceptances`
//      — com data, IP e user agent, para valor jurídico (prova de ciência).
//
// O titular aqui é o FUNCIONÁRIO (colaborador interno), não o cidadão — doc 17
// §3.3 finalidade "Operação interna / RBAC / Audit" cobre a base legal
// (Art. 7º III/IX). Ver doc 17 atualizado nesta mesma fase.
//
// Fora de escopo (doc do slot): redigir o regimento (é do advogado do
// cliente) e assinatura ICP-Brasil — o aceite em sistema com versão/data/IP
// é a evidência que este contrato produz.
// =============================================================================
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Chave canônica da política deste slot. Novas políticas (ex: aviso de
// privacidade ao cidadão, fora de escopo aqui — DPIA §3) usam outras chaves,
// mas reaproveitam o MESMO mecanismo de versão/aceite.
// ---------------------------------------------------------------------------
export const WHATSAPP_MONITORING_POLICY_KEY = 'whatsapp_monitoring';
// ---------------------------------------------------------------------------
// Chave de política — slug curto, minúsculo. Genérico (não fixado no enum de
// uma única chave) para não exigir migration a cada nova política do mesmo
// mecanismo — mas todo endpoint deste módulo valida a EXISTÊNCIA do documento
// no banco antes de qualquer efeito (fail-closed, ver service.ts do módulo).
// ---------------------------------------------------------------------------
export const POLICY_KEY_REGEX = /^[a-z][a-z0-9_]{1,63}$/;
export const policyKeyParamSchema = z.object({
    key: z
        .string()
        .regex(POLICY_KEY_REGEX, 'policyKey deve ser um slug minúsculo (letras, dígitos, "_"), começando por letra, 2-64 caracteres')
        .describe('Chave da política (ex: "whatsapp_monitoring").'),
});
// ---------------------------------------------------------------------------
// GET /api/policies/:key/status — visão do funcionário sobre a política
// vigente + o próprio aceite (ou a ausência dele).
// ---------------------------------------------------------------------------
// NOTA: este pacote (@elemento/shared-schemas) não depende de zod-openapi —
// os exemplos `.openapi({ example })` exigidos pela norma de documentação
// (doc §10) são aplicados na camada de rota (apps/api/src/modules/policies/
// schemas.ts, que importa 'zod-openapi/extend'), reenvelopando estes mesmos
// schemas. Mesmo precedente de quick-replies.ts/handoff-briefing.ts neste
// pacote — zero uso de `.openapi()` aqui.
export const policyDocumentSummarySchema = z.object({
    policyKey: z.string().describe('Chave da política.'),
    version: z.number().int().positive().describe('Versão vigente do texto (a mais recente).'),
    title: z.string().describe('Título do documento (ex: "Uso do WhatsApp corporativo").'),
    body: z.string().describe('Corpo do texto, em Markdown simples, redigido pelo cliente/advogado.'),
    publishedAt: z
        .string()
        .datetime({ offset: true })
        .describe('Quando esta versão foi cadastrada no sistema (ISO 8601).'),
});
export const myPolicyAcceptanceSchema = z
    .object({
    version: z.number().int().positive().describe('Versão que o funcionário aceitou.'),
    acceptedAt: z.string().datetime({ offset: true }).describe('Quando aceitou (ISO 8601).'),
})
    .nullable()
    .describe('null quando o funcionário nunca aceitou nenhuma versão desta política.');
export const policyStatusResponseSchema = z.object({
    document: policyDocumentSummarySchema,
    myAcceptance: myPolicyAcceptanceSchema,
    accepted: z
        .boolean()
        .describe('true somente quando myAcceptance.version === document.version — uma versão nova ' +
        'do texto reabre o aceite (accepted volta a false) até o funcionário aceitar de novo.'),
});
// ---------------------------------------------------------------------------
// POST /api/policies/:key/accept — aceite do funcionário à versão vigente.
// Sem body: o servidor deriva o ator da sessão e a versão do documento
// vigente — nada disso vem do cliente (evita forjar versão/data).
// ---------------------------------------------------------------------------
export const acceptPolicyResponseSchema = z.object({
    policyKey: z.string(),
    version: z.number().int().positive().describe('Versão que acabou de ser aceita.'),
    acceptedAt: z.string().datetime({ offset: true }).describe('Timestamp do aceite (ISO 8601).'),
});
// ---------------------------------------------------------------------------
// POST /api/policies/:key/versions — admin cadastra uma nova versão do texto
// (ex: quando o regimento definitivo do advogado chega). policies:manage.
// ---------------------------------------------------------------------------
export const createPolicyVersionBodySchema = z.object({
    title: z.string().min(1).max(200).describe('Título do documento.'),
    body: z
        .string()
        .min(1)
        .max(20000)
        .describe('Corpo do texto (Markdown simples). Trocar este campo gera versão nova.'),
});
// ---------------------------------------------------------------------------
// GET /api/policies/:key/acceptances — tela de gestão do admin: quem aceitou,
// qual versão, quando. policies:manage.
// ---------------------------------------------------------------------------
export const policyAcceptanceListQuerySchema = z.object({
    cursor: z.string().uuid().optional().describe('Cursor de paginação (id do último item).'),
    limit: z
        .preprocess((v) => (typeof v === 'string' ? Number(v) : v), z.number().int().min(1).max(200).default(50))
        .describe('Tamanho da página (máx 200).'),
});
export const policyAcceptanceListItemSchema = z.object({
    id: z.string().uuid(),
    userId: z.string().uuid().describe('UUID do funcionário que aceitou.'),
    userFullName: z
        .string()
        .nullable()
        .describe('Nome do funcionário no momento da consulta. null se o usuário não existir mais.'),
    policyVersion: z.number().int().positive(),
    acceptedAt: z.string().datetime({ offset: true }),
    ip: z.string().nullable().describe('IP capturado no aceite (derivado de X-Forwarded-For).'),
    userAgent: z.string().nullable(),
});
export const policyAcceptanceListResponseSchema = z.object({
    data: z.array(policyAcceptanceListItemSchema),
    nextCursor: z.string().uuid().nullable(),
});
//# sourceMappingURL=policies.js.map